import { useCallback, useState } from "react";

/**
 * Retires the "New" divider once catching up is over — the user engages the
 * composer (typing, opening the mention picker, adding a chip), or settles at
 * the bottom of the list. Otherwise the line would sit there for the rest of
 * the panel session.
 *
 * Retiring keeps the anchor and flags the row instead of dropping it, so the
 * divider can dissolve in place rather than vanishing and reflowing the
 * messages below.
 *
 * The dismissal sticks: clearing the input does not bring the line back, or it
 * would flicker in and out as the composer empties and refills. A genuinely new
 * anchor (another thread, or a reopen landing on newer replies) is a new
 * catch-up point and shows the line again.
 */
export const useUnreadDividerDismissal = (
  anchorEventId: string | undefined
): {
  unreadDividerEventId: string | undefined;
  isUnreadDividerRetired: boolean;
  retireDivider: () => void;
} => {
  const [dismissedAnchorId, setDismissedAnchorId] = useState<string | null>(null);

  // Reads the anchor at call time so the callback stays identity-stable: it
  // feeds combobox props, and a new identity per keystroke would churn them.
  const [anchorRef] = useState(() => ({ current: anchorEventId }));
  anchorRef.current = anchorEventId;

  const retireDivider = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    setDismissedAnchorId(current => (current === anchor ? current : anchor));
  }, [anchorRef]);

  return {
    unreadDividerEventId: anchorEventId,
    isUnreadDividerRetired: anchorEventId !== undefined && anchorEventId === dismissedAnchorId,
    retireDivider,
  };
};
